import { isNil, map } from 'rambda'
import { isObject, isObjectOrArray } from './guards'
import sift, { createEqualsOperation } from 'sift'
import { _ } from '@feathersjs/commons'
import sorter from './sorter'

const customExpressions = {
  $like: (queryValue, ownerQuery, options) => createEqualsOperation(
    fieldValue => fieldValue && fieldValue.includes(queryValue),
    ownerQuery,
    options),

  $ilike: (queryValue, ownerQuery, options) => {
    const lowercaseValue = queryValue.toLowerCase()

    return createEqualsOperation(
      fieldValue => fieldValue && fieldValue.toLowerCase().includes(lowercaseValue),
      ownerQuery,
      options)
  },

  $contained: (queryValue, ownerQuery, options) => createEqualsOperation(
    fieldValue => fieldValue && fieldValue.every(item => queryValue.includes(item)),
    ownerQuery,
    options),

  $contains: (queryValue, ownerQuery, options) => createEqualsOperation(
    fieldValue => fieldValue && queryValue.every(item => fieldValue.includes(item)),
    ownerQuery,
    options),

  $overlap: (queryValue, ownerQuery, options) => createEqualsOperation(
    fieldValue => fieldValue && queryValue.some(item => fieldValue.includes(item)),
    ownerQuery,
    options),
}

// wrap $like and $ilike in %
const wrapInPercent = (() => {
  const wrapValue = (() => {
    const fieldsToWrap = new Set([
      '$ilike',
      '$like',
      '$notilike',
      '$notlike',
    ])
    const excludeFromWrap = new Set([
      '$bounds',
      '$contained',
      '$contains',
      '$in',
      '$nin',
      '$overlap',
    ])

    return (value, key) => {
      if (typeof key !== 'undefined' && excludeFromWrap.has(key)) {
        return value
      }

      if (isObjectOrArray(value)) {
        if (Array.isArray(value)) return value.map(wrapInPercent)
        if (value instanceof Date) return value.toISOString()
        return wrapInPercent(value)
      }

      if (
        typeof value !== 'string' ||
        value.startsWith('%') ||
        value.endsWith('%') ||
        !fieldsToWrap.has(key)
      ) {
        return value
      }

      return `%${value}%`
    }
  })()

  return (cleanQuery) => {
    const wrapper = Array.isArray(cleanQuery)
      ? wrapInPercent
      : wrapValue

    return map(wrapper, cleanQuery)
  }
})()

export function buildServerQuery(query) {
  // TODO: make it deep
  // remove undefined values to allow using `queryParam || undefined` shorthand
  const { $server, ...cleanQuery } = Object
    .fromEntries(Object
      .entries(query)
      .filter(([_, value]) => typeof value !== 'undefined'))

  return wrapInPercent({
    ...cleanQuery,
    ...($server || {}),
  })
}

const Query = {
  _find(list, params) {
    if (typeof params === 'number') {
      return list[params]
    } else if (typeof value === 'string' && !isNaN(params)) {
      return list[parseInt(params, 10)]
    }

    const { $fetch, ...preQuery } = (params || {}).query || {}
    const { $skip, $limit, $sort, $select, ...query } = preQuery

    let values = Object
      .values(list)
      .filter(sift(query, {
        operations: customExpressions,
      }))

    if ($sort) {
      values.sort(typeof $sort === 'function' ? $sort : sorter($sort))
    }

    if ($skip) {
      values = values.slice($skip)
    }

    if (typeof $limit !== 'undefined') {
      values = values.slice(0, $limit)
    }

    if ($select) {
      values = values.map(value => _.pick(value, ...$select))
    }

    return values
  },

  find(list, params = {}) {
    if (isObject(params) && !params.query) {
      params = {
        query: params,
      }
    }

    // Call the internal find with query parameter that include pagination
    return this._find(list, params)
  },
}

export default (list, _state) => (params, options) => {
  const isSingle = options && options.isSingle
  const result = Query.find(list, params)

  if (isSingle === true) {
    if (result && result.length) {
      return result[0]
    } else if (isObject(result)) {
      return result
    }

    return null
  }

  return result
}

export const pruneQuery = (() => {
  const { fromEntries, entries } = Object
  const empty = new Set([
    '$contained',
    '$contains',
    '$in',
    '$nin',
    '$overlap',
  ])
  const nulls = new Set([
    '$gt',
    '$gte',
    '$lt',
    '$lte',
  ])

  const pruneQueryParameter = ([key, value]) => {
    if (empty.has(key) && !value.length) return
    if (nulls.has(key) && isNil(value)) return

    return [key, value]
  }

  return query => fromEntries(entries(query)
    .map(([key, value]) => {
      if (value === undefined) return
      if (!isObject(value)) return [key, value]

      const pruned = entries(value)
        .map(pruneQueryParameter)
        .filter(Boolean)

      if (!pruned.length) return

      return [
        key,
        fromEntries(pruned),
      ]
    })
    .filter(Boolean))
})()
